import React, { FunctionComponent, PropsWithChildren, useState } from 'react';

import { useGetTableEntriesByFeatureId } from '../../../../shared/hooks/use-get-table-entries-by-feature-id/use-get-table-entries-by-feature-id.hook';
import { useResetPageNumberAfterFiltering } from '../../../../shared/hooks/use-reset-page-number-after-filtering/use-reset-page-number-after-filtering.hook';
import { useSetFeatureId } from '../../../../shared/hooks/use-set-feature-id/use-set-feature-id.hook';
import { onChangeTableRowsPerPage } from '../../../../shared/utils/on-change-table-rows-per-page/on-change-table-rows-per-page.util';
import { onTablePageChange } from '../../../../shared/utils/on-table-page-change/on-table-page-change.util';
import { useAppDispatch } from '../../../../store/hooks';
import Box from '../../../shared/components/ui/Box';
import SDCTable from '../../../shared/components/ui/Table/components/table/SDCTable';
import { getPaginationConfig } from '../../../shared/components/ui/Table/utils/get-pagination-config.util';
import Typography from '../../../shared/components/ui/Typography/Typography';
import { bulkActionHandler } from '../../../shared/utils/bulk-action-handler/bulk-action-handler.util';
import { castTo } from '../../../shared/utils/cast-to';
import { expandRowAction } from '../../../shared/utils/expand-row-action/expand-row-action.util';
import { useIsExceptedApp } from '../../hooks/use-find-app-name-by-app-id-from-release.hook';
import { ReleaseModel } from '../../models/release.model';
import { releasesActions } from '../../store/slices/releases.slice';
import { editAppRelease } from '../../thunks/edit-app-release.thunk';
import { getAppReleasesTotalCount } from '../../thunks/get-app-releases-total-count.thunk';
import ReleasesForm from '../ReleasesForm/ReleasesForm';
import { ExceptedAppsReleaseFormWrapped } from '../excepted-apps-release-form/excepted-apps-release-form';
import { useGetModalAndReleases } from './hooks/use-get-modal-and-releases/use-get-modal-and-releases.hook';
import { useGetReleasesTableConfigHook } from './hooks/use-get-releases-table-config/use-get-releases-table-config.hook';
import { ReleasesTableProps } from './models/releases-table-props.model';
import { getSortHandler } from './utils/get-sort-handler/get-sort-handler.util';
import { releasesTableColumnsDef } from './utils/releases-table-columns-def';

const ReleasesTable: FunctionComponent<ReleasesTableProps> = ({
  isFormOpen,
  changeFormState,
}: PropsWithChildren<ReleasesTableProps>): JSX.Element => {
  const { tableRows, isLoading, appId, count, page, rowsPerPage } =
    useGetReleasesTableConfigHook();

  const dispatch = useAppDispatch();

  const [ids, setIds] = useState<number[]>([]);

  const isExceptedApp = useIsExceptedApp();

  const modalComponent = useGetModalAndReleases(ids, appId);

  useSetFeatureId(releasesActions.setAppId, parseInt(appId as string, 10));

  useGetTableEntriesByFeatureId(
    getAppReleasesTotalCount,
    parseInt(appId as string, 10)
  );

  useResetPageNumberAfterFiltering(
    count,
    page,
    rowsPerPage,
    releasesActions.resetPagination
  );

  return (
    <>
      <Box
        sx={{
          height: '100%',
          padding: 0,
          marginTop: '48px',
        }}
        data-testid="app-releases-table"
      >
        {tableRows?.length > 0 && (
          <SDCTable
            ariaLabel={'apps-table'}
            bulkActions={true}
            bulkActionHandler={bulkActionHandler(setIds)}
            checkboxSelection={true}
            columns={releasesTableColumnsDef}
            expandableRows={true}
            expandRowAction={expandRowAction(changeFormState)}
            closeAllRows={isFormOpen}
            changePrompt={true}
            expandableComponent={({ data }) => {
              return !isExceptedApp ? (
                <ReleasesForm
                  data={castTo<ReleaseModel>(data)}
                  submitThunk={editAppRelease}
                  isFormOpen={false}
                />
              ) : (
                <ExceptedAppsReleaseFormWrapped
                  data={castTo<ReleaseModel>(data)}
                  submitThunk={editAppRelease}
                  isFormOpen={false}
                />
              );
            }}
            paginationConfig={getPaginationConfig(
              count,
              rowsPerPage,
              page,
              onTablePageChange(
                dispatch,
                rowsPerPage,
                releasesActions.addPagination
              ),
              onChangeTableRowsPerPage(dispatch, releasesActions.addPagination)
            )}
            rows={tableRows}
            stickyHeader={true}
            sortHandler={getSortHandler}
            sx={{
              tr: {
                'th, td': {
                  '&:first-of-type': {
                    width: '29px',
                  },

                  '&:not(:first-of-type):not(:last-of-type)': {
                    paddingRight: '20px',
                  },
                },
              },
            }}
          />
        )}

        {tableRows?.length === 0 && !isLoading && !isFormOpen && (
          <Typography
            sx={{
              margin: 'auto',
              width: 'fit-content',
              fontSize: '24px',
              fontWeight: 1,
              color: theme => theme.palette.appGrey.darkGray,
            }}
          >
            Currently there are no releases
          </Typography>
        )}
      </Box>

      {modalComponent}
    </>
  );
};

export default ReleasesTable;
